// src/components/modals/MailModal.jsx
import { useEffect, useState } from "react";
import { PATHS } from "../../config/permissionsPaths";
import useAuth from "../../hooks/useAuth";
import Modal from "./Modal";
import { BtnGeneral } from "../../styles/Buttons";
import { toast } from "react-toastify";
import { mailApi } from "../../api/mailApi";
import { usePatientData } from "../../context/PatientDataContext";
import icons from "../../assets/icons/iconMap";

const MailModal = ({ isOpen, onClose, id }) => {
  const [mails, setMails] = useState([]);
  const [newMail, setNewMail] = useState("");
  const [isPrincipal, setIsPrincipal] = useState(false);
  const [editingMail, setEditingMail] = useState(null);
  const [saving, setSaving] = useState(false);
  const { hasPermission } = useAuth();

  const { fetchPatientData } = usePatientData();

  const loadMails = async () => {
    if (!id) return;
    try {
      const data = await mailApi.getMails(id);
      setMails(data || []);
    } catch (err) {
      console.error("Error al cargar correos:", err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadMails();
      resetForm();
    }
  }, [isOpen, id]);

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const resetForm = () => {
    setNewMail("");
    setIsPrincipal(false);
    setEditingMail(null);
  };

  const handleAddMail = async () => {
    if (saving) return;
    if (!newMail) return toast.error("Diligenciar el campo correo");
    if (!isValidEmail(newMail)) return toast.error("Ingrese un correo válido");

    try {
      setSaving(true);
      const rs = await mailApi.saveMail({
        autoid: id,
        correo: newMail,
        principal: isPrincipal ? 1 : 0,
      });
      
      if(!rs) return;
      
      resetForm();
      loadMails();
      fetchPatientData();
      onClose();
    } catch (err) {
      console.error("Error al guardar correo:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMail = async () => {
    if (saving) return;
    if (!newMail) return toast.error("Diligenciar el campo correo");
    if (!isValidEmail(newMail)) return toast.error("Ingrese un correo válido");

    try {
      setSaving(true);
      const rs = await mailApi.saveMail({
        id: editingMail.id,
        autoid: id,
        correo: newMail,
        principal: isPrincipal ? 1 : 0,
      });
      
      if(!rs) return;
      
      resetForm();
      loadMails();
      fetchPatientData();
      toast.success("Correo actualizado correctamente");
    } catch (err) {
      console.error("Error al actualizar correo:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditMail = (mail) => {
    setEditingMail(mail);
    setNewMail(mail.correo);
    setIsPrincipal(String(mail.principal) === "1");
  };

  const handleInactiveMail = async (mailId) => {
    try {
      const data = await mailApi.inactiveMail(mailId);

      if (!data.success) return toast.error(data.mensaje);

      loadMails();
    } catch (err) {
      console.error("Error al eliminar correo:", err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Correos del Paciente" width="400px">
      <div>
        {hasPermission(PATHS.CREAR_CORREO) && (
          <>
            <input
              type="email"
              value={newMail}
              onChange={(e) => setNewMail(e.target.value)}
              placeholder="Ingrese el correo"
              style={{
                width: "100%",
                padding: "3px 15px",
                marginBottom: "10px",
                border: "1px solid #ccc",
                borderRadius: "8px",
                fontSize: "0.95rem",
              }}
            />

            <label style={{ display: "block", marginBottom: "10px" }}>
              ¿Es principal?{" "}
              <input
                type="checkbox"
                checked={isPrincipal}
                onChange={(e) => setIsPrincipal(e.target.checked)}
              />
            </label>

            <div style={{ display: "flex", gap: "10px" }}>
              <BtnGeneral 
                bg={editingMail ? "blue" : "green"} 
                padding="7px" 
                radius="8px" 
                disabled={saving}
                onClick={editingMail ? handleUpdateMail : handleAddMail}
              >
                {saving ? "Guardando..." : editingMail ? "Actualizar" : "Guardar"}
              </BtnGeneral>

              {editingMail && (
                <BtnGeneral 
                  bg="gray" 
                  padding="7px" 
                  radius="8px" 
                  disabled={saving}
                  onClick={resetForm}
                >
                  Cancelar
                </BtnGeneral>
              )}
            </div>

            <hr style={{ margin: "15px 0" }} />
          </>
        )}

        <h5 style={{ marginBottom: "5px", color: "#444" }}>Correos registrados:</h5>

        {mails.length > 0 ? (
          <ul style={{ listStyle: "none", padding: 0 }}>
            {mails.map((p) => (
              <li
                key={p.id}
                style={{
                  marginBottom: "3px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0px 32px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                }}
              >
                <span>
                  {p.correo} {p.principal === "1" ? "(Principal)" : ""}
                </span>

                <div style={{ display: "flex", gap: "8px",alignItems: "center" }}>
                  {hasPermission(PATHS.CREAR_CORREO) && (
                    <img
                    src={icons["pencil"]}
                    alt="Editar Correo"
                    title="Editar Correo"
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      width: 14,
                      height: 14,
                    }}
                    className="hover"
                    onClick={() => handleEditMail(p)}
                  />
                  )}

                  {hasPermission(PATHS.ELIMINAR_CORREO) && (
                    <button
                      onClick={() => handleInactiveMail(p.id)}
                      style={{ 
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        fontSize: "14px",
                        padding: 0,
                        lineHeight: 1,
                      }}
                      className="hover"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No hay correos registrados.</p>
        )}
      </div>
    </Modal>
  );
};

export default MailModal;